/**
 * Static demo data used by TemplatePreviewPage.
 * 
 * No Supabase calls are made when these are in use — all data is resolved
 * synchronously from this file, giving instant, always-complete previews
 * regardless of whether the user's real store has any content.
 */

import type { Store } from '@/services/stores'
import type { Product } from '@/services/products'
import type { Category } from '@/services/categories'
import { assertPreviewVerticalProductCategoryCohesion } from '@/data/previewCatalogIntegrity'
import {
  demoCategoriesDropship,
  demoCategoriesEditorial,
  demoCategoriesEditorialSleek,
  demoCategoriesFashion,
  demoCategoriesFashionV2,
  demoCategoriesFood,
  demoCategoriesFunnel,
  demoCategoriesGeneral,
  demoCategoriesHomeClassic,
  demoCategoriesKids,
  demoCategoriesPerfume,
  demoCategoriesPharmacy,
  demoCategoriesPremiumTech,
  demoProductsDropship,
  demoProductsEditorial,
  demoProductsEditorialSleek,
  demoProductsFashion,
  demoProductsFashionV2,
  demoProductsFood,
  demoProductsFunnel,
  demoProductsGeneral,
  demoProductsHomeClassic,
  demoProductsKids,
  demoProductsPerfume,
  demoProductsPharmacy,
  demoProductsPremiumTech,
} from '@/data/previewDemoVerticals'

/** Which static dataset template preview uses (`TemplatePreviewPage` + `PreviewModeProvider`). One catalog per template for brand isolation. */
export type PreviewDemoCatalog =
  | 'beauty'
  | 'fashion'
  | 'fashionV2'
  | 'electronics'
  | 'electronicsPremium'
  | 'homeDecor'
  | 'food'
  | 'kids'
  | 'pharmacy'
  | 'perfume'
  | 'general'
  | 'dropship'
  | 'funnel'
  | 'editorialFashion'
  | 'editorialSleek'
  | 'homeClassic'
  | 'premiumTech'
  | 'premiumCosmetics'
  /** Unknown template — empty preview catalog (no silent fallback to beauty). */
  | 'unmapped'

/**
 * Every `storeTemplates` id maps to its own preview catalog (unique demo brand / SKUs).
 */
export const PREVIEW_TEMPLATE_CATALOG_MAP: Record<string, PreviewDemoCatalog> = {
  beauty: 'beauty',
  fashion: 'fashion',
  fashionv2: 'fashionV2',
  electronics: 'electronics',
  general: 'general',
  food: 'food',
  kids: 'kids',
  'home-decor': 'homeClassic',
  pharmacy: 'pharmacy',
  perfume: 'perfume',
  'premium-tech': 'premiumTech',
  'premium-electronics-v1': 'electronicsPremium',
  'editorial-fashion-v1': 'editorialFashion',
  'single-product-funnel-v1': 'funnel',
  'home-decor-pinterest-v1': 'homeDecor',
  'dropship-velocity-v1': 'dropship',
  'premium-cosmetics-v1': 'premiumCosmetics',
  'premium-sleek-editorial-v1': 'editorialSleek',
}

/**
 * Maps each registered store template id to the demo vertical used for categories / products
 * in `/preview-template/:id/ref/...`.
 */
export function previewCatalogForStoreTemplateId(templateId: string): PreviewDemoCatalog {
  const id = templateId.trim()
  if (!id) {
    if (import.meta.env.DEV) {
      throw new Error('[preview] Empty templateId — cannot resolve preview catalog.')
    }
    return 'unmapped'
  }
  /** Offline canonical block clusters — use general demo vertical for products/categories. */
  if (id.startsWith('canonical-')) {
    return 'general'
  }
  const catalog = PREVIEW_TEMPLATE_CATALOG_MAP[id]
  if (!catalog) {
    if (import.meta.env.DEV) {
      throw new Error(
        `[preview] Missing preview catalog mapping for template "${id}". Add it to PREVIEW_TEMPLATE_CATALOG_MAP in demoStorefrontData.ts.`,
      )
    }
    return 'unmapped'
  }
  return catalog
}

// ─── Demo store ──────────────────────────────────────────────────────────────

export const DEMO_STORE_ID = '__preview__'
export const DEMO_STORE_SLUG = 'demo'

export const demoStore: Store = {
  id: DEMO_STORE_ID,
  user_id: '__preview__',
  name: 'FastSell Demo',
  slug: DEMO_STORE_SLUG,
  description: 'La boutique de démonstration FastSell',
  template_id: 1,
  layout_config: null,
  logo_url: null,
  custom_colors: {},
  custom_fonts: {},
  custom_css: null,
  custom_domain: null,
  subdomain: null,
  meta_title: 'FastSell Demo',
  meta_description: 'Aperçu du template FastSell',
  meta_image: null,
  facebook_url: null,
  instagram_url: null,
  tiktok_url: null,
  contact_email: 'demo@fastsell.tn',
  contact_phone: '+216 70 000 000',
  contact_address: 'Tunis, Tunisie',
  is_published: true,
  is_maintenance_mode: false,
  total_visits: 0,
  integration_settings: {},
  created_at: '2024-01-01T00:00:00Z',
  updated_at: '2024-01-01T00:00:00Z',
  published_at: '2024-01-01T00:00:00Z',
}

/** Per-template preview branding — each storefront template reads as a different company in `/preview-template/...`. */
export const PREVIEW_TEMPLATE_STORE_BRAND: Record<
  string,
  Pick<Store, 'name' | 'description' | 'meta_title' | 'meta_description'>
> = {
  beauty: {
    name: 'Lumen Peau Lab',
    description: 'Soins sensoriels et routines éclat — boutique démo.',
    meta_title: 'Lumen Peau Lab',
    meta_description: 'Beauté & soins (aperçu template)',
  },
  fashion: {
    name: 'Atelier Minuit',
    description: 'Silhouettes studio et matières premium — démo.',
    meta_title: 'Atelier Minuit',
    meta_description: 'Mode prêt-à-porter (aperçu)',
  },
  fashionv2: {
    name: 'Capsule Studio FV2',
    description: 'Collection limitée, coupe nette — vitrine démo.',
    meta_title: 'Capsule Studio FV2',
    meta_description: 'Fashion studio (aperçu)',
  },
  electronics: {
    name: 'Circuit Nine',
    description: 'Tech grand public — vitrine démo.',
    meta_title: 'Circuit Nine',
    meta_description: 'Électronique (aperçu)',
  },
  general: {
    name: 'Marché Mixte',
    description: 'Promos, maison et idées cadeaux — démo.',
    meta_title: 'Marché Mixte',
    meta_description: 'Généraliste (aperçu)',
  },
  food: {
    name: 'Halte Fraîche',
    description: 'Panier local et épicerie fine — démo.',
    meta_title: 'Halte Fraîche',
    meta_description: 'Alimentaire (aperçu)',
  },
  kids: {
    name: 'Petit Vent',
    description: 'Jouets et univers 0–12 ans — démo.',
    meta_title: 'Petit Vent',
    meta_description: 'Enfants (aperçu)',
  },
  'home-decor': {
    name: 'Ligne Salon',
    description: 'Mobilier et textiles pour la maison — démo.',
    meta_title: 'Ligne Salon',
    meta_description: 'Décoration classique (aperçu)',
  },
  pharmacy: {
    name: 'Pharmacie Source',
    description: 'Santé, compléments et matériel — démo.',
    meta_title: 'Pharmacie Source',
    meta_description: 'Parapharmacie (aperçu)',
  },
  perfume: {
    name: 'Maison Sillage',
    description: 'Parfums, oud et bougies — démo.',
    meta_title: 'Maison Sillage',
    meta_description: 'Parfumerie (aperçu)',
  },
  'premium-tech': {
    name: 'Nexa Lab',
    description: 'Cases, audio et smart home — démo premium.',
    meta_title: 'Nexa Lab',
    meta_description: 'Tech premium (aperçu)',
  },
  'premium-electronics-v1': {
    name: 'Volt & Co',
    description: 'Électronique certifiée — vitrine démo.',
    meta_title: 'Volt & Co',
    meta_description: 'Électronique premium (aperçu)',
  },
  'editorial-fashion-v1': {
    name: 'Maison Minuit',
    description: 'Horlogerie & accessoires—collection démo luxe.',
    meta_title: 'Maison Minuit',
    meta_description: 'Montres et maroquinerie premium (aperçu)',
  },
  'single-product-funnel-v1': {
    name: 'Atelier Core',
    description: 'Une référence, un tunnel — démo D2C.',
    meta_title: 'Atelier Core',
    meta_description: 'Tunnel mono-SKU (aperçu)',
  },
  'home-decor-pinterest-v1': {
    name: 'Atelier Carré',
    description: 'Inspiration déco et pièces signature — démo.',
    meta_title: 'Atelier Carré',
    meta_description: 'Home Pinterest (aperçu)',
  },
  'dropship-velocity-v1': {
    name: 'Velocity Pick',
    description: 'Gadgets et essentials livrés vite — démo.',
    meta_title: 'Velocity Pick',
    meta_description: 'Dropship (aperçu)',
  },
  'premium-cosmetics-v1': {
    name: 'Maison Lumière',
    description: 'Rituels haute concentration — démo luxe.',
    meta_title: 'Maison Lumière',
    meta_description: 'Cosmétiques premium (aperçu)',
  },
  'premium-sleek-editorial-v1': {
    name: 'Podium Silhouette',
    description: 'Éditorial minimal — lookbook démo.',
    meta_title: 'Podium Silhouette',
    meta_description: 'Fashion sleek (aperçu)',
  },
}

export function getPreviewDemoStore(templateId: string): Pick<
  Store,
  'name' | 'description' | 'meta_title' | 'meta_description'
> {
  const id = templateId.trim()
  return PREVIEW_TEMPLATE_STORE_BRAND[id] ?? {
    name: demoStore.name,
    description: demoStore.description,
    meta_title: demoStore.meta_title,
    meta_description: demoStore.meta_description,
  }
}

const NOW = '2024-01-01T00:00:00Z'

// ─── Demo categories ──────────────────────────────────────────────────────────

export const demoCategories: Category[] = [
  {
    id: 'cat-1',
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Soins du visage',
    slug: 'soins-visage',
    description: 'Crèmes, sérums et masques pour prendre soin de votre peau',
    image_url: '/templates/local-webp/bty-cat-soins.webp',
    position: 1,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 4,
  },
  {
    id: 'cat-2',
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Parfums',
    slug: 'parfums',
    description: 'Eau de parfum et eaux de toilette',
    image_url: '/templates/local-webp/bty-cat-parfums.webp',
    position: 2,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 3,
  },
  {
    id: 'cat-3',
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Maquillage',
    slug: 'maquillage',
    description: 'Rouge à lèvres, fond de teint et accessoires de beauté',
    image_url: '/templates/local-webp/bty-cat-maquillage.webp',
    position: 3,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 3,
  },
  {
    id: 'cat-4',
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Corps & Bain',
    slug: 'corps-bain',
    description: 'Lotions, huiles et soins pour le corps',
    image_url: '/templates/local-webp/bty-cat-corps.webp',
    position: 4,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 2,
  },
]

// ─── Demo categories — electronics (template preview: premium-electronics-v1) ─

/** UUID-shaped so shop filters (`category_id` query) work in template preview. */
const ECAT_PHONES = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaa1'
const ECAT_LAPTOPS = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaa2'
const ECAT_AUDIO = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaa3'
const ECAT_SMART = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaa4'

export const demoCategoriesElectronics: Category[] = [
  {
    id: ECAT_PHONES,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Smartphones',
    slug: 'smartphones',
    description: 'Derniers smartphones, pliants et accessoires',
    image_url: '/templates/local-webp/elx-cat-phones.webp',
    position: 1,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 3,
  },
  {
    id: ECAT_LAPTOPS,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Laptops & tablets',
    slug: 'laptops-tablets',
    description: 'Stations de travail productives et créatives',
    image_url: '/templates/local-webp/elx-cat-laptops.webp',
    position: 2,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 2,
  },
  {
    id: ECAT_AUDIO,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Audio',
    slug: 'audio',
    description: 'Casques, écouteurs et enceintes',
    image_url: '/templates/local-webp/elx-cat-audio.webp',
    position: 3,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 2,
  },
  {
    id: ECAT_SMART,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Smart home',
    slug: 'smart-home',
    description: 'Sécurité, éclairage et assistants',
    image_url: '/templates/local-webp/elx-cat-smart.webp',
    position: 4,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 1,
  },
]

/** Premium electronics (`premium-electronics-v1`) — same nav slugs as consumer electronics, distinct SKUs. */
const EPX_PHONES = 'dddddddd-dddd-4ddd-8ddd-dddddddddd01'
const EPX_LAPTOPS = 'dddddddd-dddd-4ddd-8ddd-dddddddddd02'
const EPX_AUDIO = 'dddddddd-dddd-4ddd-8ddd-dddddddddd03'
const EPX_SMART = 'dddddddd-dddd-4ddd-8ddd-dddddddddd04'

export const demoCategoriesElectronicsPremium: Category[] = [
  {
    id: EPX_PHONES,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Smartphones',
    slug: 'smartphones',
    description: 'Certified handsets — demo preview',
    image_url: '/templates/local-webp/elx-cat-phones.webp',
    position: 1,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 3,
  },
  {
    id: EPX_LAPTOPS,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Laptops & tablets',
    slug: 'laptops-tablets',
    description: 'Workstations — demo preview',
    image_url: '/templates/local-webp/elx-cat-laptops.webp',
    position: 2,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 2,
  },
  {
    id: EPX_AUDIO,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Audio',
    slug: 'audio',
    description: 'Headphones and speakers',
    image_url: '/templates/local-webp/elx-cat-audio.webp',
    position: 3,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 2,
  },
  {
    id: EPX_SMART,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Smart home',
    slug: 'smart-home',
    description: 'Cameras and assistants',
    image_url: '/templates/local-webp/elx-cat-smart.webp',
    position: 4,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 1,
  },
]

// ─── Demo products ─────────────────────────────────────────────────────────────

export const demoProducts: Product[] = [
  {
    id: 'bty-prod-1',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Crème hydratante intense',
    slug: 'beauty-creme-hydratante-intense',
    price: 49.9,
    compare_at_price: 69.9,
    stock: 30,
    status: 'active',
    image: '/templates/local-webp/bty-prod-1.webp',
    images: ['/templates/local-webp/bty-prod-1.webp'],
    orders_count: 142,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: 'cat-1',
    product_type: 'cosmetics',
  },
  {
    id: 'bty-prod-2',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Sérum vitamine C éclat',
    slug: 'beauty-serum-vitamine-c-eclat',
    price: 79.9,
    compare_at_price: 99,
    stock: 18,
    status: 'active',
    image: '/templates/local-webp/bty-prod-2.webp',
    images: ['/templates/local-webp/bty-prod-2.webp'],
    orders_count: 98,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: 'cat-1',
    product_type: 'cosmetics',
  },
  {
    id: 'bty-prod-3',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Huile de rose musquée',
    slug: 'beauty-huile-rose-musquee',
    price: 59.9,
    stock: 25,
    status: 'active',
    image: '/templates/local-webp/bty-prod-3.webp',
    images: ['/templates/local-webp/bty-prod-3.webp'],
    orders_count: 67,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: 'cat-4',
    product_type: 'cosmetics',
  },
  {
    id: 'bty-prod-4',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Eau de parfum "Rose Orientale"',
    slug: 'beauty-edp-rose-orientale',
    price: 129,
    compare_at_price: 160,
    stock: 12,
    status: 'active',
    image: '/templates/local-webp/bty-prod-4.webp',
    images: ['/templates/local-webp/bty-prod-4.webp'],
    orders_count: 204,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: 'cat-2',
    product_type: 'cosmetics',
  },
  {
    id: 'bty-prod-5',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Fond de teint longue tenue',
    slug: 'beauty-fond-teint-longue-tenue',
    price: 45,
    stock: 40,
    status: 'active',
    image: '/templates/local-webp/bty-prod-5.webp',
    images: ['/templates/local-webp/bty-prod-5.webp'],
    orders_count: 88,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: 'cat-3',
    product_type: 'cosmetics',
  },
  {
    id: 'bty-prod-6',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Masque purifiant à l\'argile',
    slug: 'beauty-masque-purifiant-argile',
    price: 34.9,
    compare_at_price: 44.9,
    stock: 55,
    status: 'active',
    image: '/templates/local-webp/bty-prod-6.webp',
    images: ['/templates/local-webp/bty-prod-6.webp'],
    orders_count: 120,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: 'cat-1',
    product_type: 'cosmetics',
  },
  {
    id: 'bty-prod-7',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Rouge à lèvres mat "Sable Doré"',
    slug: 'beauty-rouge-sable-dore',
    price: 29.9,
    stock: 60,
    status: 'active',
    image: '/templates/local-webp/bty-prod-7.webp',
    images: ['/templates/local-webp/bty-prod-7.webp'],
    orders_count: 77,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: 'cat-3',
    product_type: 'cosmetics',
  },
  {
    id: 'bty-prod-8',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Lotion corps au lait d\'amande',
    slug: 'beauty-lotion-corps-lait-amande',
    price: 39.9,
    stock: 35,
    status: 'active',
    image: '/templates/local-webp/bty-prod-8.webp',
    images: ['/templates/local-webp/bty-prod-8.webp'],
    orders_count: 55,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: 'cat-4',
    product_type: 'cosmetics',
  },
]

/** Maison Lumière — distinct luxury line for `premium-cosmetics-v1` (not a clone of `beauty`). */
const PMC_FACE = 'pmc-cat-face'
const PMC_MAKE = 'pmc-cat-makeup'
const PMC_SCENT = 'pmc-cat-scent'
const PMC_BODY = 'pmc-cat-body'

export const demoCategoriesPremiumCosmetics: Category[] = [
  {
    id: PMC_FACE,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Soins visage',
    slug: 'soins-visage',
    description: 'Rituels haute concentration',
    image_url: '/templates/premium-cosmetics-v1/gal-1.jpg',
    position: 1,
    is_active: true,
    created_at: NOW,
    updated_at: NOW,
    product_count: 2,
  },
  {
    id: PMC_MAKE,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Maquillage',
    slug: 'maquillage',
    description: 'Textures satin et mat',
    image_url: '/templates/premium-cosmetics-v1/gal-2.jpg',
    position: 2,
    is_active: true,
    created_at: NOW,
    updated_at: NOW,
    product_count: 2,
  },
  {
    id: PMC_SCENT,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Parfums',
    slug: 'parfums',
    description: 'Extraits et brumes',
    image_url: '/templates/premium-cosmetics-v1/gal-3.jpg',
    position: 3,
    is_active: true,
    created_at: NOW,
    updated_at: NOW,
    product_count: 2,
  },
  {
    id: PMC_BODY,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Corps & bain',
    slug: 'corps-bain',
    description: 'Huiles et laits',
    image_url: '/templates/premium-cosmetics-v1/gal-4.jpg',
    position: 4,
    is_active: true,
    created_at: NOW,
    updated_at: NOW,
    product_count: 2,
  },
]

export const demoProductsPremiumCosmetics: Product[] = [
  {
    id: 'pmc-prod-1',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Élixir nuit — peptide 5 %',
    slug: 'lumiere-elixir-nuit-peptide',
    price: 112,
    compare_at_price: 139,
    stock: 40,
    status: 'active',
    image: '/templates/premium-cosmetics-v1/elixir-nuit-peptide-5-custom.png',
    images: ['/templates/premium-cosmetics-v1/elixir-nuit-peptide-5-custom.png'],
    orders_count: 88,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PMC_FACE,
    product_type: 'cosmetics',
  },
  {
    id: 'pmc-prod-2',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Baume premier — barrière céramide',
    slug: 'lumiere-baume-barriere-ceramide',
    price: 78,
    stock: 55,
    status: 'active',
    image: '/templates/premium-cosmetics-v1/baume-premier-barriere-ceramide-custom.png',
    images: ['/templates/premium-cosmetics-v1/baume-premier-barriere-ceramide-custom.png'],
    orders_count: 64,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PMC_FACE,
    product_type: 'cosmetics',
  },
  {
    id: 'pmc-prod-3',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Stylo lumière — teinte Porcelaine',
    slug: 'lumiere-stylo-teinte-porcelaine',
    price: 54,
    stock: 70,
    status: 'active',
    image: '/templates/premium-cosmetics-v1/stylo-lumiere-teinte-porcelaine-custom.png',
    images: ['/templates/premium-cosmetics-v1/stylo-lumiere-teinte-porcelaine-custom.png'],
    orders_count: 120,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PMC_MAKE,
    product_type: 'cosmetics',
  },
  {
    id: 'pmc-prod-4',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Palette ombres — Quartz froid',
    slug: 'lumiere-palette-quartz-froid',
    price: 92,
    compare_at_price: 110,
    stock: 28,
    status: 'active',
    image: '/templates/premium-cosmetics-v1/palette-ombres-quartz-froid-custom.png',
    images: ['/templates/premium-cosmetics-v1/palette-ombres-quartz-froid-custom.png'],
    orders_count: 45,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PMC_MAKE,
    product_type: 'cosmetics',
  },
  {
    id: 'pmc-prod-5',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Extrait — Ombre de figuier',
    slug: 'lumiere-extrait-ombre-figuier',
    price: 210,
    stock: 15,
    status: 'active',
    image: '/templates/premium-cosmetics-v1/extrait-ombre-de-figuier-custom.png',
    images: ['/templates/premium-cosmetics-v1/extrait-ombre-de-figuier-custom.png'],
    orders_count: 33,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PMC_SCENT,
    product_type: 'cosmetics',
  },
  {
    id: 'pmc-prod-6',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Brume cheveux — Tubéreuse',
    slug: 'lumiere-brume-cheveux-tubereuse',
    price: 48,
    stock: 60,
    status: 'active',
    image: '/templates/premium-cosmetics-v1/brume-cheveux-tubereuse-custom.png',
    images: ['/templates/premium-cosmetics-v1/brume-cheveux-tubereuse-custom.png'],
    orders_count: 72,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PMC_SCENT,
    product_type: 'cosmetics',
  },
  {
    id: 'pmc-prod-7',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Huile sèche — Pétales de coton',
    slug: 'lumiere-huile-seche-petales',
    price: 67,
    stock: 44,
    status: 'active',
    image: '/templates/premium-cosmetics-v1/huile-seche-petales-de-coton-custom.png',
    images: ['/templates/premium-cosmetics-v1/huile-seche-petales-de-coton-custom.png'],
    orders_count: 51,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PMC_BODY,
    product_type: 'cosmetics',
  },
  {
    id: 'pmc-prod-8',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Gel douche — Sel rose & bergamote',
    slug: 'lumiere-gel-douche-sel-rose',
    price: 36,
    stock: 90,
    status: 'active',
    image: '/templates/premium-cosmetics-v1/gel-douche-sel-rose-bergamote-custom.png',
    images: ['/templates/premium-cosmetics-v1/gel-douche-sel-rose-bergamote-custom.png'],
    orders_count: 140,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PMC_BODY,
    product_type: 'cosmetics',
  },
]

export const demoProductsElectronics: Product[] = [
  {
    id: 'elx-prod-1',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Ultraphone S24 — titane bleu',
    slug: 'elx-galaxy-s24-ultra-titanium-blue',
    price: 4299,
    compare_at_price: 4599,
    stock: 14,
    status: 'active',
    image: '/templates/local-webp/elx-prod-1.webp',
    images: ['/templates/local-webp/elx-prod-1.webp'],
    orders_count: 312,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: ECAT_PHONES,
    product_type: 'electronics',
  },
  {
    id: 'elx-prod-2',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Pliable Z Fold 5 — noir phantom',
    slug: 'elx-galaxy-z-fold-5-phantom-black',
    price: 5499,
    compare_at_price: null,
    stock: 6,
    status: 'active',
    image: '/templates/local-webp/elx-prod-2.webp',
    images: ['/templates/local-webp/elx-prod-2.webp'],
    orders_count: 198,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: ECAT_PHONES,
    product_type: 'electronics',
  },
  {
    id: 'elx-prod-3',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Clapet Z Flip 5 — menthe',
    slug: 'elx-galaxy-z-flip-5-mint',
    price: 2899,
    stock: 22,
    status: 'active',
    image: '/templates/local-webp/elx-prod-3.webp',
    images: ['/templates/local-webp/elx-prod-3.webp'],
    orders_count: 267,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: ECAT_PHONES,
    product_type: 'electronics',
  },
  {
    id: 'elx-prod-4',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Portable 9 Pro — 15" OLED',
    slug: 'elx-notebook-9-pro-15-oled',
    price: 3999,
    compare_at_price: 4499,
    stock: 9,
    status: 'active',
    image: '/templates/local-webp/elx-prod-4.webp',
    images: ['/templates/local-webp/elx-prod-4.webp'],
    orders_count: 156,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: ECAT_LAPTOPS,
    product_type: 'electronics',
  },
  {
    id: 'elx-prod-5',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Ultraportable 14" — aluminium',
    slug: 'elx-ultrabook-14-aluminium',
    price: 3299,
    stock: 11,
    status: 'active',
    image: '/templates/local-webp/elx-prod-5.webp',
    images: ['/templates/local-webp/elx-prod-5.webp'],
    orders_count: 124,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: ECAT_LAPTOPS,
    product_type: 'electronics',
  },
  {
    id: 'elx-prod-6',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Écouteurs ANC Pro',
    slug: 'elx-anc-earbuds-pro',
    price: 449,
    compare_at_price: 599,
    stock: 48,
    status: 'active',
    image: '/templates/local-webp/elx-prod-6.webp',
    images: ['/templates/local-webp/elx-prod-6.webp'],
    orders_count: 401,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: ECAT_AUDIO,
    product_type: 'electronics',
  },
  {
    id: 'elx-prod-7',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Casque studio circum-aural',
    slug: 'elx-studio-over-ear-headphones',
    price: 799,
    stock: 18,
    status: 'active',
    image: '/templates/local-webp/elx-prod-7.webp',
    images: ['/templates/local-webp/elx-prod-7.webp'],
    orders_count: 89,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: ECAT_AUDIO,
    product_type: 'electronics',
  },
  {
    id: 'elx-prod-8',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Caméra sécurité 2K',
    slug: 'elx-smart-security-camera-2k',
    price: 329,
    stock: 35,
    status: 'active',
    image: '/templates/local-webp/elx-prod-8.webp',
    images: ['/templates/local-webp/elx-prod-8.webp'],
    orders_count: 210,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: ECAT_SMART,
    product_type: 'electronics',
  },
]

/** Certified-reseller lineup for `premium-electronics-v1` — démo vitrine ; distinct de `elx-*`. */
export const demoProductsElectronicsPremium: Product[] = [
  {
    id: 'epx-prod-1',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Smartphone Corvair X9 Pro 5G — édition performance',
    slug: 'epx-nexa-s24-ultra-titanium-blue',
    price: 4399,
    compare_at_price: 4699,
    stock: 12,
    status: 'active',
    image: '/templates/premium-electronics-v1/epx-nexa-s24-ultra-titanium-blue.png',
    images: ['/templates/premium-electronics-v1/epx-nexa-s24-ultra-titanium-blue.png'],
    orders_count: 280,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: EPX_PHONES,
    product_type: 'electronics',
  },
  {
    id: 'epx-prod-2',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Smartphone Sovi S7 — 4 caméras, écran amélioré',
    slug: 'epx-nexa-z-fold-6-phantom',
    price: 5599,
    compare_at_price: null,
    stock: 5,
    status: 'active',
    image: '/templates/premium-electronics-v1/epx-nexa-z-fold-6-phantom.png',
    images: [
      '/templates/premium-electronics-v1/epx-nexa-z-fold-6-phantom.png',
      '/templates/premium-electronics-v1/epx-nexa-z-fold-6-phantom-b.png',
    ],
    orders_count: 190,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: EPX_PHONES,
    product_type: 'electronics',
  },
  {
    id: 'epx-prod-3',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Pixalr 5 Pro — obsidienne',
    slug: 'epx-pixalr-5-pro-obsidian',
    price: 2999,
    stock: 20,
    status: 'active',
    image: '/templates/premium-electronics-v1/epx-pixalr-5-pro.png',
    images: [
      '/templates/premium-electronics-v1/epx-pixalr-5-pro.png',
      '/templates/premium-electronics-v1/epx-pixalr-5-pro-angle.png',
    ],
    orders_count: 250,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: EPX_PHONES,
    product_type: 'electronics',
  },
  {
    id: 'epx-prod-4',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Dellea Ultimate Performance Thin Profile Laptop',
    slug: 'epx-voltbook-pro-15-oled',
    price: 4099,
    compare_at_price: 4599,
    stock: 8,
    status: 'active',
    image: '/templates/premium-electronics-v1/epx-voltbook-pro-15-oled.png',
    images: ['/templates/premium-electronics-v1/epx-voltbook-pro-15-oled.png'],
    orders_count: 140,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: EPX_LAPTOPS,
    product_type: 'electronics',
  },
  {
    id: 'epx-prod-5',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Portable gaming Alieware AW-X16 — RGB & finition métal',
    slug: 'epx-alieware-aw-x16-rgb',
    price: 3399,
    stock: 10,
    status: 'active',
    image: '/templates/premium-electronics-v1/epx-alieware-aw-x16.png',
    images: [
      '/templates/premium-electronics-v1/epx-alieware-aw-x16.png',
      '/templates/premium-electronics-v1/epx-alieware-aw-x16-top.png',
    ],
    orders_count: 110,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: EPX_LAPTOPS,
    product_type: 'electronics',
  },
  {
    id: 'epx-prod-6',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Buds FE True Wireless Earbuds',
    slug: 'epx-pulsebuds-pro-anc',
    price: 459,
    compare_at_price: 589,
    stock: 50,
    status: 'active',
    image: '/templates/premium-electronics-v1/epx-pulsebuds-pro-anc.png',
    images: [
      '/templates/premium-electronics-v1/epx-pulsebuds-pro-anc.png',
      '/templates/premium-electronics-v1/epx-pulsebuds-pro-anc-b.png',
    ],
    orders_count: 380,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: EPX_AUDIO,
    product_type: 'electronics',
  },
  {
    id: 'epx-prod-7',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Pixalr Buds Pro — ANC, anthracite & boîtier',
    slug: 'epx-pixalr-buds-pro-charcoal',
    price: 819,
    stock: 16,
    status: 'active',
    image: '/templates/premium-electronics-v1/epx-pixalr-buds-pro-charcoal.png',
    images: [
      '/templates/premium-electronics-v1/epx-pixalr-buds-pro-charcoal.png',
      '/templates/premium-electronics-v1/epx-pixalr-buds-lineup.png',
    ],
    orders_count: 82,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: EPX_AUDIO,
    product_type: 'electronics',
  },
  {
    id: 'epx-prod-8',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Samsen Smart Camera C700 4K',
    slug: 'epx-homecam-2k-night',
    price: 339,
    stock: 32,
    status: 'active',
    image: '/templates/premium-electronics-v1/epx-homecam-2k-night.png',
    images: ['/templates/premium-electronics-v1/epx-homecam-2k-night.png'],
    orders_count: 200,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: EPX_SMART,
    product_type: 'electronics',
  },
]

/** IDs from `demoProductsElectronics` — use in electronics / pro tech templates. `premium-tech` preview uses `demoProductsPremiumTech` (same ids, distinct titles). */
export const DEMO_ELECTRONICS_TEMPLATE_BUNDLE_IDS: string[] = ['elx-prod-1', 'elx-prod-2', 'elx-prod-3']
export const DEMO_ELECTRONICS_TEMPLATE_SPOTLIGHT_ID = 'elx-prod-1'

// ─── Demo categories — home decor (template preview: home-decor-pinterest-v1) ─

const HCAT_LIVING = 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbb1'
const HCAT_LIGHTING = 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbb2'
const HCAT_TEXTILES = 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbb3'
const HCAT_TABLETOP = 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbb4'

export const demoCategoriesHomeDecor: Category[] = [
  {
    id: HCAT_LIVING,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Salon & assises',
    slug: 'salon-assises',
    description: 'Fauteuils, tables basses et rangements',
    image_url: '/templates/home-decor-pinterest-v1/carre-016-800.webp',
    position: 1,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 2,
  },
  {
    id: HCAT_LIGHTING,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Luminaires',
    slug: 'luminaires',
    description: 'Lampes de table, suspensions et ambiance',
    image_url: '/templates/home-decor-pinterest-v1/carre-006-800.webp',
    position: 2,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 2,
  },
  {
    id: HCAT_TEXTILES,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Textiles & linge',
    slug: 'textiles-linge',
    description: 'Plaids, coussins et lin de table',
    image_url: '/templates/home-decor-pinterest-v1/carre-003-800.webp',
    position: 3,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 2,
  },
  {
    id: HCAT_TABLETOP,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Art de la table',
    slug: 'art-de-la-table',
    description: 'Céramique, carafes et détails de table',
    image_url: '/templates/home-decor-pinterest-v1/carre-012-800.webp',
    position: 4,
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    product_count: 2,
  },
]

export const demoProductsHomeDecor: Product[] = [
  {
    id: 'hdc-prod-1',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Coussin tissé — sable',
    slug: 'coussin-tisse-sable',
    price: 89,
    compare_at_price: 115,
    stock: 24,
    status: 'active',
    image: '/templates/home-decor-pinterest-v1/product-coussin-tisse-sable-800.webp',
    images: ['/templates/home-decor-pinterest-v1/product-coussin-tisse-sable-800.webp'],
    orders_count: 56,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: HCAT_TEXTILES,
    product_type: 'home',
  },
  {
    id: 'hdc-prod-2',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Lampe de table — céramique & abat-jour lin',
    slug: 'lampe-table-ceramique-ivoire',
    price: 189,
    compare_at_price: 229,
    stock: 14,
    status: 'active',
    image: '/templates/home-decor-pinterest-v1/product-lampe-table-ceramique-ivoire-800.webp',
    images: ['/templates/home-decor-pinterest-v1/product-lampe-table-ceramique-ivoire-800.webp'],
    orders_count: 41,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: HCAT_LIGHTING,
    product_type: 'home',
  },
  {
    id: 'hdc-prod-3',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Plaid mohair — camel',
    slug: 'plaid-mohair-camel',
    price: 249,
    stock: 18,
    status: 'active',
    image: '/templates/home-decor-pinterest-v1/product-plaid-mohair-camel-800.webp',
    images: ['/templates/home-decor-pinterest-v1/product-plaid-mohair-camel-800.webp'],
    orders_count: 33,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: HCAT_TEXTILES,
    product_type: 'home',
  },
  {
    id: 'hdc-prod-4',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Plante décorative — cache-pot crème',
    slug: 'vase-travertin-grand',
    price: 165,
    compare_at_price: 199,
    stock: 11,
    status: 'active',
    image: '/templates/home-decor-pinterest-v1/product-vase-travertin-grand-800.webp',
    images: ['/templates/home-decor-pinterest-v1/product-vase-travertin-grand-800.webp'],
    orders_count: 72,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: HCAT_TABLETOP,
    product_type: 'home',
  },
  {
    id: 'hdc-prod-5',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Chaise lounge bouclé — crème',
    slug: 'chaise-lounge-boucle-creme',
    price: 1299,
    stock: 4,
    status: 'active',
    image: '/templates/home-decor-pinterest-v1/product-chaise-lounge-boucle-creme-800.webp',
    images: ['/templates/home-decor-pinterest-v1/product-chaise-lounge-boucle-creme-800.webp'],
    orders_count: 19,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: HCAT_LIVING,
    product_type: 'home',
  },
  {
    id: 'hdc-prod-6',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Photophores verre dépoli — trio Lumière',
    slug: 'photophores-verre-depoli',
    price: 59.9,
    compare_at_price: 79,
    stock: 40,
    status: 'active',
    image: '/templates/home-decor-pinterest-v1/product-photophores-verre-depoli-800.webp',
    images: ['/templates/home-decor-pinterest-v1/product-photophores-verre-depoli-800.webp'],
    orders_count: 88,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: HCAT_TABLETOP,
    product_type: 'home',
  },
  {
    id: 'hdc-prod-7',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Étagère 3 niveaux — vert laqué',
    slug: 'etagere-murale-chene-90',
    price: 349,
    stock: 9,
    status: 'active',
    image: '/templates/home-decor-pinterest-v1/product-etagere-murale-chene-90-800.webp',
    images: ['/templates/home-decor-pinterest-v1/product-etagere-murale-chene-90-800.webp'],
    orders_count: 27,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: HCAT_LIVING,
    product_type: 'home',
  },
  {
    id: 'hdc-prod-8',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Carafe grès émaillé — 1 L',
    slug: 'carafe-gres-emaille-1l',
    price: 79,
    stock: 22,
    status: 'active',
    image: '/templates/home-decor-pinterest-v1/product-carafe-gres-emaille-1l-800.webp',
    images: ['/templates/home-decor-pinterest-v1/product-carafe-gres-emaille-1l-800.webp'],
    orders_count: 44,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: HCAT_TABLETOP,
    product_type: 'home',
  },
]

/** Deep-clone so preview consumers cannot mutate shared module-level demo rows. */
function clonePreviewArray<T>(items: T[]): T[] {
  if (items.length === 0) return []
  return structuredClone(items) as T[]
}

export function getPreviewCategories(catalog: PreviewDemoCatalog): Category[] {
  if (catalog === 'unmapped') return []
  if (catalog === 'electronics') return clonePreviewArray(demoCategoriesElectronics)
  if (catalog === 'electronicsPremium') return clonePreviewArray(demoCategoriesElectronicsPremium)
  if (catalog === 'homeDecor') return clonePreviewArray(demoCategoriesHomeDecor)
  if (catalog === 'fashion') return clonePreviewArray(demoCategoriesFashion)
  if (catalog === 'fashionV2') return clonePreviewArray(demoCategoriesFashionV2)
  if (catalog === 'food') return clonePreviewArray(demoCategoriesFood)
  if (catalog === 'kids') return clonePreviewArray(demoCategoriesKids)
  if (catalog === 'pharmacy') return clonePreviewArray(demoCategoriesPharmacy)
  if (catalog === 'perfume') return clonePreviewArray(demoCategoriesPerfume)
  if (catalog === 'general') return clonePreviewArray(demoCategoriesGeneral)
  if (catalog === 'dropship') return clonePreviewArray(demoCategoriesDropship)
  if (catalog === 'funnel') return clonePreviewArray(demoCategoriesFunnel)
  if (catalog === 'editorialFashion') return clonePreviewArray(demoCategoriesEditorial)
  if (catalog === 'editorialSleek') return clonePreviewArray(demoCategoriesEditorialSleek)
  if (catalog === 'homeClassic') return clonePreviewArray(demoCategoriesHomeClassic)
  if (catalog === 'premiumTech') return clonePreviewArray(demoCategoriesPremiumTech)
  if (catalog === 'premiumCosmetics') return clonePreviewArray(demoCategoriesPremiumCosmetics)
  return clonePreviewArray(demoCategories)
}

export function getPreviewProducts(catalog: PreviewDemoCatalog): Product[] {
  if (catalog === 'unmapped') return []
  if (catalog === 'electronics') return clonePreviewArray(demoProductsElectronics)
  if (catalog === 'electronicsPremium') return clonePreviewArray(demoProductsElectronicsPremium)
  if (catalog === 'homeDecor') return clonePreviewArray(demoProductsHomeDecor)
  if (catalog === 'fashion') return clonePreviewArray(demoProductsFashion)
  if (catalog === 'fashionV2') return clonePreviewArray(demoProductsFashionV2)
  if (catalog === 'food') return clonePreviewArray(demoProductsFood)
  if (catalog === 'kids') return clonePreviewArray(demoProductsKids)
  if (catalog === 'pharmacy') return clonePreviewArray(demoProductsPharmacy)
  if (catalog === 'perfume') return clonePreviewArray(demoProductsPerfume)
  if (catalog === 'general') return clonePreviewArray(demoProductsGeneral)
  if (catalog === 'dropship') return clonePreviewArray(demoProductsDropship)
  if (catalog === 'funnel') return clonePreviewArray(demoProductsFunnel)
  if (catalog === 'editorialFashion') return clonePreviewArray(demoProductsEditorial)
  if (catalog === 'editorialSleek') return clonePreviewArray(demoProductsEditorialSleek)
  if (catalog === 'homeClassic') return clonePreviewArray(demoProductsHomeClassic)
  if (catalog === 'premiumTech') return clonePreviewArray(demoProductsPremiumTech)
  if (catalog === 'premiumCosmetics') return clonePreviewArray(demoProductsPremiumCosmetics)
  return clonePreviewArray(demoProducts)
}

/** Slugs that resolve on the category PLP for this preview vertical — use in template JSON instead of hardcoding. */
export function getPreviewCategorySlugs(catalog: PreviewDemoCatalog): string[] {
  return getPreviewCategories(catalog)
    .map((c) => (typeof c.slug === 'string' ? c.slug.trim() : ''))
    .filter(Boolean)
}

export function getPreviewProductsByCategorySlug(
  catalog: PreviewDemoCatalog,
  categorySlug: string | undefined,
): Product[] {
  const cats = getPreviewCategories(catalog)
  const all = getPreviewProducts(catalog)
  const slug = typeof categorySlug === 'string' ? categorySlug.trim() : ''
  if (!slug) return all
  const c = cats.find((x) => x.slug === slug)
  // Unknown slug (e.g. marketing link from another vertical): do not fall back to the full
  // catalog — that made every template look like it shared the same products.
  if (!c) return []
  return all.filter((p) => p.category_id === c.id)
}

export function getPreviewProductBySlug(
  catalog: PreviewDemoCatalog,
  productSlug: string | undefined,
): Product | null {
  const all = getPreviewProducts(catalog)
  const slug = typeof productSlug === 'string' ? productSlug.trim() : ''
  if (!slug) return null
  return all.find((p) => p.slug === slug) ?? null
}

/** Convenience: get demo products for a given category ID (or all if undefined). */
export function getDemoProductsByCategory(categoryId?: string): Product[] {
  if (!categoryId) return demoProducts
  return demoProducts.filter((p) => p.category_id === categoryId)
}

if (import.meta.env.DEV) {
  assertPreviewVerticalProductCategoryCohesion('beauty (default demo)', demoCategories, demoProducts)
  assertPreviewVerticalProductCategoryCohesion('electronics', demoCategoriesElectronics, demoProductsElectronics)
  assertPreviewVerticalProductCategoryCohesion('electronicsPremium', demoCategoriesElectronicsPremium, demoProductsElectronicsPremium)
  assertPreviewVerticalProductCategoryCohesion('homeDecor', demoCategoriesHomeDecor, demoProductsHomeDecor)
  assertPreviewVerticalProductCategoryCohesion('premiumTech', demoCategoriesPremiumTech, demoProductsPremiumTech)
  assertPreviewVerticalProductCategoryCohesion('premiumCosmetics', demoCategoriesPremiumCosmetics, demoProductsPremiumCosmetics)
  assertPreviewVerticalProductCategoryCohesion('fashionV2', demoCategoriesFashionV2, demoProductsFashionV2)
  assertPreviewVerticalProductCategoryCohesion('editorialFashion', demoCategoriesEditorial, demoProductsEditorial)
  assertPreviewVerticalProductCategoryCohesion('editorialSleek', demoCategoriesEditorialSleek, demoProductsEditorialSleek)
  void Promise.all([
    import('@/data/previewTemplateIsolation'),
    import('@/data/storeTemplates'),
  ]).then(([{ runPreviewIsolationChecks }, stMod]) => {
    const storeTemplatesList = stMod?.storeTemplates
    if (!Array.isArray(storeTemplatesList)) return
    runPreviewIsolationChecks(
      {
        previewCatalogForStoreTemplateId,
        getPreviewProducts,
        getPreviewCategorySlugs,
      },
      storeTemplatesList,
    )
  })
}
